// components/zone-levels-popover/zone-levels-popover.component.ts
import {
    Component,
    Input,
    ViewChild,
    ElementRef,
    OnInit,
    OnDestroy,
    ChangeDetectionStrategy,
    ChangeDetectorRef, Output, EventEmitter
} from '@angular/core';
import { DxPopoverComponent } from 'devextreme-angular';
import { ZoneIndicatorApiService } from '../model/model';
import {DetectMethodChanges, isValidNumber, isVoid} from "../../utils";
import {AccessControlService} from "../../access-control-service.class";
import {SessionService} from "../../authentication/session-service.service";
import {CurrentZoneLevel, CurrentZoneTemplateType} from "../model/zone-template.class";
import {GetPortfolioCurrentZoneDataReply} from "../model/cqrs.class";
import {
    GenericConfirmationDialogComponent
} from "../../generic-confirmation-dialog/generic-confirmation-dialog.component";
import {CurrentZoneService} from "../model/current-zone.service";
import {MessageBusService} from "../../message-bus.service";
import {ToastrService} from "ngx-toastr";

// Published after an EXPLICIT "set as current zone" succeeds, so a live APG can update its ORION
// Zone step without a portfolio reload. Carries the value (owner ruling 2026-09-10: carry, do not
// re-fetch). Not published for the overnight rule - that path reloads the panel anyway.
export interface CurrentZoneChangedMessage {
    portfolioKey: string;
    currentZone: number;
}

const PopoverSecureElementId = '1a193ba5-837d-4a44-9b8b-76272feb8165';

interface LevelRow {
    type: 'zone' | 'level',
    level?: CurrentZoneLevel,
    zone?: number,
}

@Component({
    selector: 'ets-zone-levels-popover',
    templateUrl: './zone-levels-popover.component.html',
    styleUrls: ['./zone-levels-popover.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ZoneLevelsPopoverComponent implements OnInit, OnDestroy {


    @ViewChild('popover', { static: false }) popover: DxPopoverComponent;
    @Input() targetElement: any; // The element to attach the popover to
    @Input() symbol: string = '';
    @ViewChild('confirmationDialog') confirmationDialog: GenericConfirmationDialogComponent;

    visible: boolean = false;
    loading: boolean = false;

    private _currentZoneData: GetPortfolioCurrentZoneDataReply;
    get currentZoneData(): GetPortfolioCurrentZoneDataReply {
        return this._currentZoneData;
    }

    @Input()
    set currentZoneData(value: GetPortfolioCurrentZoneDataReply) {
        this._currentZoneData = value;
        this.updateLevels();
    }


    type1Levels: LevelRow[] = [];
    type2Levels: LevelRow[] = [];

    @Output() zoneChanged = new EventEmitter<void>();

    private mouseEnterHandler: any;
    private mouseLeaveHandler: any;
    private hideTimeout: any;
    private showTimeout: any;
    private closePrice: number;

    constructor(
        private apiService: ZoneIndicatorApiService,
        private elementRef: ElementRef,
        private _changeDetector: ChangeDetectorRef,
        private _aclService: AccessControlService,
        private _sessionService: SessionService,
        private readonly _currentZoneService: CurrentZoneService,
        private readonly _messageBus: MessageBusService,
        private readonly _toastr: ToastrService,
    ) {}

    async ngOnInit() {
        this.attachEventListeners();
        this.closePrice = await this.apiService.getClosePrice();
    }

    private updateLevels() {

        if (isVoid(this.currentZoneData)) {
            return;
        }

        this.updateType1Levels();
        this.updateType2Levels();
        this.updateType3Levels();

    }

    // #region CR-20260913-001 - Type 3, its own list, its own labels, nothing of Type 2

    /**
     * One row per generated line, the zone among them. Read-only: a Type 3 zone starts at the
     * template's starting price and moves by the backend rule alone (owner ruling 2026-09-13,
     * Q1); brackets are rule inputs, not levels, and are not drawn (Q6).
     */
    type3Levels: CurrentZoneLevel[] = [];

    private updateType3Levels() {
        if (this.currentZoneData.type !== 'Type 3') {
            this.type3Levels = [];
            return;
        }

        const lines = (this.currentZoneData.levels || []).slice();

        const zoneIndex = lines.findIndex(l => l.price === this.currentZoneData.currentZone);

        this.type3Levels = ZoneLevelsPopoverComponent.centreWindow(
            lines, zoneIndex, ZoneLevelsPopoverComponent.LevelWindowSize);
    }

    /**
     * Rows shown (Type 2 and Type 3) when the ladder is longer than the window. Odd on purpose
     * (owner ruling 2026-09-15): the zone row sits in the centre with an equal count above and
     * below. An even cap can never centre the zone - 8 gave 4 above / 3 below.
     */
    static readonly LevelWindowSize = 9;

    /**
     * Type 1 rows interleave levels with midpoint zones, so its window is wider. Odd as well:
     * the zone row at the centre, 7 rows (4 levels + 3 zones) each side.
     */
    static readonly Type1WindowSize = 15;

    /**
     * The window of rows to show, centred on the zone row. Shorter ladders show whole. When the
     * zone sits near an end, the window shifts to keep its full size ("do its best" - the far
     * side gets the spare rows). No zone row: the top of the ladder.
     */
    static centreWindow<T>(rows: T[], zoneIndex: number, windowSize: number): T[] {
        if (rows.length <= windowSize) {
            return rows;
        }

        if (zoneIndex < 0) {
            return rows.slice(0, windowSize);
        }

        let start = zoneIndex - Math.floor(windowSize / 2);
        let end = start + windowSize;

        if (start < 0) {
            start = 0;
            end = windowSize;
        }

        if (end > rows.length) {
            end = rows.length;
            start = rows.length - windowSize;
        }

        return rows.slice(start, end);
    }

    getType3LevelLabel(level: CurrentZoneLevel): string {
        if (!isValidNumber(level.price, true) || isVoid(this.currentZoneData)) {
            return undefined;
        }

        if (!isValidNumber(this.currentZoneData.currentZone, true)) {
            return 'Undefined';
        }

        if (level.price === this.currentZoneData.currentZone) {
            return 'Zone';
        }

        return level.price > this.currentZoneData.currentZone ? 'Resistance' : 'Support';
    }

    getType3LevelClass(level: CurrentZoneLevel): string {
        switch (this.getType3LevelLabel(level)) {
            case 'Resistance':
                return 'resistance-level';
            case 'Zone':
                return 'zone-level';
            case 'Support':
                return 'support-level';
            default:
                return 'undefined-level';
        }
    }

    // #endregion

    private updateType1Levels() {
        this.type1Levels.length = 0;

        const levels: LevelRow[] = [];

        const originalLevels = this.currentZoneData.levels;

        for (let i = 0; i < originalLevels.length; i++) {

            const currentLevel = originalLevels[i];
            const levelRow: LevelRow = {
                type: 'level',
                level: currentLevel,
            };
            levels.push(levelRow);

            const nextIx = i + 1;
            if (nextIx >= originalLevels.length) {
                break;
            }

            const nextLevel = originalLevels[nextIx];

            let zone = (nextLevel.price + currentLevel.price) / 2;
            zone /= 5;
            zone = Math.round(zone);
            zone *= 5;

            const zoneRow: LevelRow = {
                type: 'zone',
                zone: zone,
            };

            levels.push(zoneRow);
        }

        // A Type 1 zone is a midpoint ROW, not a level - look it up among the zone rows.
        // (Until 2026-09-15 this searched level rows, never matched, and the window then
        // fell out of a splice(start, endIndex) whose second argument is a count.)
        const zoneIndex = levels.findIndex(x => x.type === 'zone' && this.isCurrentZone(x.zone));

        this.type1Levels = ZoneLevelsPopoverComponent.centreWindow(
            levels, zoneIndex, ZoneLevelsPopoverComponent.Type1WindowSize);
    }

    private updateType2Levels() {
        this.type2Levels.length = 0;

        const originalLevels = this.currentZoneData.levels;

        const levels2 = originalLevels.map(x => {

            const isZone = x.price === this.currentZoneData.currentZone;

            const lr: LevelRow = {
                type: 'level',
                level: x,
                zone: isZone ? x.price : null,
            };

            return lr;
        });

        const zoneIndex = levels2.findIndex(level => isValidNumber(level.zone, true));

        this.type2Levels = ZoneLevelsPopoverComponent.centreWindow(
            levels2, zoneIndex, ZoneLevelsPopoverComponent.LevelWindowSize);
    }

    ngOnDestroy() {
        this.detachEventListeners();
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
        }
    }

    clearTimeouts() {
        clearTimeout(this.showTimeout);
        clearTimeout(this.hideTimeout);
    }

    private attachEventListeners() {

        if (!this._aclService.isSecureElementAvailable(PopoverSecureElementId)) {
            if (!this._sessionService.isSuperUser) {
                return;
            }
        }

        if (!this.targetElement) return;

        const element = this.getTargetElement();
        if (!element) return;

        this.mouseEnterHandler = this.onMouseEnter.bind(this);
        this.mouseLeaveHandler = this.onMouseLeave.bind(this);

        element.addEventListener('mouseenter', this.mouseEnterHandler);
        element.addEventListener('mouseleave', this.mouseLeaveHandler);
    }

    private detachEventListeners() {
        const element = this.getTargetElement();
        if (!element) return;

        element.removeEventListener('mouseenter', this.mouseEnterHandler);
        element.removeEventListener('mouseleave', this.mouseLeaveHandler);
    }

    private getTargetElement(): HTMLElement {
        if (typeof this.targetElement === 'string') {
            return document.querySelector(this.targetElement);
        } else if (this.targetElement instanceof ElementRef) {
            return this.targetElement.nativeElement;
        } else if (this.targetElement instanceof HTMLElement) {
            return this.targetElement;
        }
        return null;
    }

    private onMouseEnter() {
        // Clear any pending hide timeout
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
            this.hideTimeout = null;
        }

        this.showTimeout = setTimeout(() => {
            this.show();
        }, 500);

    }

    private onMouseLeave(event: MouseEvent) {
        // Check if mouse is moving to the popover itself
        const relatedTarget = event.relatedTarget as HTMLElement;
        if (relatedTarget && this.isPopoverElement(relatedTarget)) {
            return; // Don't hide if moving to popover
        }

        if (this.showTimeout) {
            clearTimeout(this.showTimeout);
            this.showTimeout = null;
        }

        // Add a small delay before hiding to prevent flicker
        this.hideTimeout = setTimeout(() => {
            this.hide();
        }, 300);
    }

    private isPopoverElement(element: HTMLElement): boolean {
        // Check if the element is part of the popover
        const popoverElement = this.elementRef.nativeElement.querySelector('.dx-overlay-content');
        return popoverElement && popoverElement.contains(element);
    }

    @DetectMethodChanges()
    show() {
        this.visible = true;
    }

    @DetectMethodChanges()
    hide() {
        this.visible = false;
    }

    onPopoverMouseEnter() {
        // Clear hide timeout when mouse enters popover
        if (this.hideTimeout) {
            clearTimeout(this.hideTimeout);
            this.hideTimeout = null;
        }
    }

    onPopoverMouseLeave() {
        // Hide when mouse leaves popover
        this.hideTimeout = setTimeout(() => {
            this.hide();
        }, 300);
    }

    getLevelClass(level: CurrentZoneLevel): string {
        const label = this.getLevelLabel(level);

        if (label === 'Resistance') {
            return 'resistance-level';
        }

        if (label === 'Zone') {
            return 'zone-level';
        }

        if (label === 'Support') {
            return 'support-level';
        }

        return 'undefined-level'
    }

    getLevelLabel(level: CurrentZoneLevel): string {
        if (!isValidNumber(level.price, true)) {
            return undefined;
        }

        if (isVoid(this.currentZoneData)) {
            return undefined;
        }

        if (this.currentZoneData.type === 'Type 2') {

            if (!isValidNumber(this.currentZoneData.currentZone, true)) {
                return 'Undefined';
            }

            const ix = this.currentZoneData.levels.findIndex(l => l.price === this.currentZoneData.currentZone);

            if (ix < 0) {
                return 'Undefined';
            }

            if (level.price === this.currentZoneData.currentZone) {
                return 'Zone';
            }

            return level.price > this.currentZoneData.currentZone ? 'Resistance' : 'Support';

        } else if (this.currentZoneData.type === 'Type 1') {

            if (!isValidNumber(this.currentZoneData.currentZone, true)) {
                return 'Undefined';
            }

            return level.price > this.currentZoneData.currentZone ? 'Resistance' : 'Support';
        }
    }

    formatPrice(price: number): string {
        if (!isValidNumber(price, true)) {
            return '';
        }
        return price.toFixed(2);
    }

    isCurrentZone(zone: number) {
        if (!isValidNumber(zone, true)) {
            return false;
        }
        const currentZone = this._currentZoneData?.currentZone;
        if (!isValidNumber(currentZone, true)) {
            return false;
        }
        // (Before 2026-09-15: `zone - undefined || 0` collapsed NaN to 0 and matched EVERY zone row.)
        return Math.abs(zone - currentZone) < Number.EPSILON;
    }

    /**
     * Owner ruling 2026-09-15: setting a zone by hand (Type 2 or Type 3) is for super users only.
     * The backend refuses everyone else; this hides the pick so a trader never sees a refusal.
     */
    get canSetZone(): boolean {
        return this._sessionService.isSuperUser === true;
    }

    @DetectMethodChanges({isAsync: true})
    async setAsCurrentZone(level: CurrentZoneLevel) {
        await this.setZoneByHand(level, pfId => this._currentZoneService.setAsCurrentZone(pfId, level.price));
    }

    /** Owner ruling 2026-09-15: the Type 3 pick, on its own command. Only grid lines are offered, so the level is always valid. */
    @DetectMethodChanges({isAsync: true})
    async setAsCurrentZone3(level: CurrentZoneLevel) {
        await this.setZoneByHand(level, pfId => this._currentZoneService.setAsCurrentZone3(pfId, level.price));
    }

    private async setZoneByHand(level: CurrentZoneLevel, send: (pfId: string) => Promise<void>) {
        if (!this.canSetZone) {
            return;
        }

        try {
            const text = [
                'Are you sure you want to set the level',
                `[${level.price}]`,
                'as current zone?'
            ];

            await this.confirmationDialog.show(text);

        } catch {
            return;
        }

        const pfId = this.currentZoneData.portfolioId;
        try {
            await send(pfId);
            this.currentZoneData.currentZone = level.price;
            this.updateLevels();
            // Only after the backend accepted it: a failed set must leave listeners untouched.
            this._messageBus.publish<CurrentZoneChangedMessage>({
                topic: 'CurrentZoneChanged',
                payload: {portfolioKey: pfId, currentZone: level.price}
            });
            // The parent re-reads the portfolio: for Type 3 the backend regenerates the ladder
            // around the new zone, and only a fresh read shows the lines that appeared.
            this.zoneChanged.emit();
        } catch {
            // A refused set must be SEEN (owner, 2026-09-15): a silent failure looked like
            // "nothing happened". Whatever the cause - not a super user, a level off the grid,
            // a shell without the handler - the zone did not change; say so.
            this._toastr.error(`Could not set ${level.price} as the current zone`);
        }
    }
}